import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchHostingRunSteps, fetchHostingRuns, isLiveRun, type HostingRun, type HostingRunStep } from '../../lib/hosting';

const POLL_MS = 4000;
const HISTORY = 12;

/**
 * An account's checklist runs, newest first, plus the latest run's steps.
 * Polls while the latest run is queued or running and stops when it finishes;
 * `onFinish` fires once on that transition.
 */
export function useHostingRuns(accountId: string | undefined, onFinish?: () => void) {
  const [runs, setRuns] = useState<HostingRun[]>([]);
  const [steps, setSteps] = useState<HostingRunStep[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    try {
      const r = await fetchHostingRuns(accountId, HISTORY);
      const s = r[0] ? await fetchHostingRunSteps(r[0].id) : [];
      setRuns(r);
      setSteps(s);
      setError(null);
    } catch (err) {
      console.error('Error loading hosting runs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load checklist runs');
    } finally {
      setLoaded(true);
    }
  }, [accountId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const latest = runs[0] ?? null;
  const live = isLiveRun(latest);

  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, refresh]);

  const wasLive = useRef(false);
  const finished = useRef(onFinish);
  useEffect(() => {
    finished.current = onFinish;
  }, [onFinish]);
  useEffect(() => {
    if (wasLive.current && !live) finished.current?.();
    wasLive.current = live;
  }, [live]);

  return { runs, latest, steps, live, loaded, error, refresh };
}
